import React, { useEffect, useState, ReactNode, CSSProperties } from "react";
import Zana from "./Zana";
import "./FleetShell.css";

export interface FleetNavItem {
  key: string;
  label: string;
  onClick: () => void;
  dot?: boolean;
  locked?: boolean;
}

export interface FleetShellAccount {
  /** First word of the org name when the rail is open (e.g. "Hidden"). */
  orgLabel: string;
  /** Short role under the org name — e.g. "Owner". */
  roleLabel?: string;
  /** Two-letter monogram in the avatar. */
  initials?: string;
  onLogout: () => void;
}

interface FleetShellProps {
  active: string;
  navItems: FleetNavItem[];
  /** The existing top-bar right cluster (search, bell, premium, settings). */
  headerRight: ReactNode;
  /** When a bus is selected, the top bar shows a back button + title instead of the logo. */
  detail?: { title: string; subtitle?: string; onBack: () => void } | null;
  /** Zana tier (Premium/Enterprise) → the whole shell shifts from blue to Zana purple. */
  zana?: boolean;
  /** Override the logo wordmark. Trasse Families is the same shell but not the
   *  same product — a household console must not say "Bus Management". */
  brand?: { title: string; subtitle: string };
  /** Sidebar footer: org + sign-out (replaces the old Collapse control). */
  account?: FleetShellAccount | null;
  children: ReactNode;
}

const ACCENT = "#2563eb";
const ACCENT_SOFT = "rgba(37,99,235,.10)";
const ZANA_ACCENT = "#6f4ff0";
const ZANA_ACCENT_SOFT = "rgba(111,79,240,.12)";
const BLUE_LOGO = "linear-gradient(140deg,#2f6bff,#1e40af)";
const ZANA_LOGO = "linear-gradient(140deg,#8a63f0,#5a34c9)";
// Below this viewport width the sidebar auto-collapses to an icon rail; at or
// above it the labels smoothly expand back in.
const RAIL_BREAKPOINT = 1024;

const BRAND_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11h16M5 16h14a1 1 0 0 0 1-1V7a3 3 0 0 0-3-3H7a3 3 0 0 0-3 3v8a1 1 0 0 0 1 1Z" /><circle cx="8" cy="18" r="1.4" /><circle cx="16" cy="18" r="1.4" /></svg>
);

const HAMBURGER_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
);

const LOGOUT_ICON = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>
);

/** Spotlight hooks for the nav. Zana's takeover scripts name these ids, and the
 *  backend refuses any target that is not in its allow-list — so the two lists
 *  have to agree. See zana/engine/takeover.js SPOTLIGHT_TARGETS. */
const ZT_NAV: Record<string, string> = {
  dashboard: "navDash",
  fleet: "navFleet",
  accounts: "navAccounts",
  monthlyReport: "navMonthlyReport",
  trips: "navTrips",
  liveMap: "navLiveMap",
  fuel: "navFuel",
  fuelPrices: "navFuelPrices",
  maintenance: "navMaintenance",
  analytics: "navAnalytics",
  zanaChat: "navZanaChat",
};

const ICONS: Record<string, ReactNode> = {
  dashboard: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7.5" height="7.5" rx="1.5" /><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" /><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" /></svg>,
  fleet: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11h16M5 16h14a1 1 0 0 0 1-1V7a3 3 0 0 0-3-3H7a3 3 0 0 0-3 3v8a1 1 0 0 0 1 1Z" /><circle cx="8" cy="18" r="1.4" /><circle cx="16" cy="18" r="1.4" /></svg>,
  accounts: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 17.5V19" /><circle cx="10" cy="8" r="3.2" /><path d="M20 19v-1.4a3.4 3.4 0 0 0-2.6-3.3M15.5 5.1a3.2 3.2 0 0 1 0 5.9" /></svg>,
  monthlyReport: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></svg>,
  trips: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></svg>,
  liveMap: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z" /><path d="M9 4v14M15 6v14" /></svg>,
  fuel: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v15M3 21h12M3 11h10" /><path d="M15 9l3 0M18 9v8a2 2 0 0 0 2 2 2 2 0 0 0 2-2v-8.5L19 5" /></svg>,
  fuelPrices: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v15M3 21h12" /><path d="M6 9.5h6M15 12.5h6M18 9.5v6" /></svg>,
  maintenance: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  ),
  analytics: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 14l3.5-4 3 2.5L21 6" /></svg>,
  /* Her own face, not a stock robot glyph.
     Same mistake as bug 66, where her tab wore a generic AI sparkle while
     ZanaMascot appeared on every other surface: she is a character with a
     drawn face, and the one nav item that carries her name was the one place
     showing somebody else's icon. `animated` is left off on purpose — a nav
     rail is not the place for a blinking face. */
  zanaChat: <Zana state="online" size={20} />,
  // Trasse Families rooms. Separate keys from the school console's so the two
  // navs can never collide on an active-state selector.
  today: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7.2V12l3.2 2" /></svg>,
  reports: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></svg>,
  hours: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4M11 13.5h5" /></svg>,
  billing: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="M2.5 10h19M6.5 15h3" /></svg>,
  settings: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3.2" /><path d="M12 2.8v2.4M12 18.8v2.4M4.7 7.6l2.1 1.2M17.2 15.2l2.1 1.2M4.7 16.4l2.1-1.2M17.2 8.8l2.1-1.2" /></svg>,
};

/** The top-bar brand (logo + wordmark). Turns Zana purple for Zana-tier orgs. */
const Brand = ({ zana, brand }: { zana?: boolean; brand?: { title: string; subtitle: string } }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 11, flex: "0 0 auto" }}>
    <div style={{ width: 38, height: 38, flex: "0 0 38px", borderRadius: 11, background: zana ? ZANA_LOGO : BLUE_LOGO, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: zana ? "0 6px 16px rgba(111,79,240,.34)" : "0 6px 16px rgba(37,99,235,.32)" }}>{BRAND_ICON}</div>
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-.2px", lineHeight: 1.1 }}>{brand?.title ?? "Trasse Fleet"}</div>
      <div style={{ fontSize: 10.5, color: "#94a3b8" }}>{brand?.subtitle ?? "Bus Management"}</div>
    </div>
  </div>
);

export default function FleetShell({ active, navItems, headerRight, detail, zana, brand, account, children }: FleetShellProps) {
  const accent = zana ? ZANA_ACCENT : ACCENT;
  const accentSoft = zana ? ZANA_ACCENT_SOFT : ACCENT_SOFT;
  const logoBg = zana ? ZANA_LOGO : BLUE_LOGO;
  // The sidebar is ALWAYS on the left. It auto-collapses to an icon rail below
  // RAIL_BREAKPOINT and expands (labels in) above it — smoothly, via CSS. The ☰
  // at the top of the sidebar toggles it manually.
  const [railOpen, setRailOpen] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth >= RAIL_BREAKPOINT
  );

  useEffect(() => {
    const apply = () => setRailOpen(window.innerWidth >= RAIL_BREAKPOINT);
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  const toggleRail = () => setRailOpen((o) => !o);

  const shellStyle = { ["--pear-accent" as string]: accent, ["--pear-accent-soft" as string]: accentSoft } as CSSProperties;
  const railBtn: CSSProperties = { display: "flex", alignItems: "center", gap: 13, padding: "11px 12px", borderRadius: 11, border: "none", cursor: "pointer", background: "transparent", color: "#475569", font: "inherit", fontSize: 13.5, fontWeight: 600, width: "100%" };

  const initials = (account?.initials || account?.orgLabel || "F").slice(0, 2).toUpperCase();

  return (
    <div className="pear-shell" data-layout="side" data-rail={railOpen ? "open" : "closed"} data-active={active} style={shellStyle}>
      {/* ===== SIDEBAR (always on the left) ===== */}
      <aside className="pear-side">
        <div className="pear-side-head">
          <button
            type="button"
            className="pear-iconbtn pear-side-toggle"
            onClick={toggleRail}
            title={railOpen ? "Collapse sidebar" : "Expand sidebar"}
            aria-label={railOpen ? "Collapse sidebar" : "Expand sidebar"}
            aria-expanded={railOpen}
          >
            {HAMBURGER_ICON}
          </button>
        </div>
        <nav style={{ display: "flex", flexDirection: "column", gap: 4, padding: "8px 14px", flex: 1, overflow: "hidden auto" }}>
          {navItems.map((it) => (
            <button key={it.key} className="pear-navbtn pear-railbtn" data-k={it.key} data-zt={ZT_NAV[it.key]} onClick={it.onClick} title={it.label} style={railBtn}>
              <span style={{ display: "flex", width: 22, flex: "0 0 22px", alignItems: "center", justifyContent: "center" }}>{ICONS[it.key]}</span>
              <span className="pear-rail-label" style={{ flex: 1, textAlign: "left" }}>{it.label}</span>
              {it.dot && <span className="pear-rail-label pear-dot" />}
              {it.locked && <span className="pear-rail-label" style={{ fontSize: 11 }}>🔒</span>}
            </button>
          ))}
        </nav>
        {account && (
          <div className="pear-side-foot">
            {/* Expanded: org + role + logout (console-style). Collapsed: logout only. */}
            <div className="pear-account pear-rail-label">
              <div
                className="pear-account-avatar"
                style={{ background: logoBg }}
                aria-hidden
              >
                {initials}
              </div>
              <div className="pear-account-text">
                <div className="pear-account-name">{account.orgLabel}</div>
                <div className="pear-account-role">{account.roleLabel || "Owner"}</div>
              </div>
              <button
                type="button"
                className="pear-logout-btn"
                onClick={account.onLogout}
                title="Sign out"
                aria-label="Sign out"
              >
                {LOGOUT_ICON}
              </button>
            </div>
            <button
              type="button"
              className="pear-logout-only"
              onClick={account.onLogout}
              title="Sign out"
              aria-label="Sign out"
            >
              {LOGOUT_ICON}
            </button>
          </div>
        )}
      </aside>

      {/* ===== CONTENT COLUMN ===== */}
      <div className="pear-main-col">
        <header className="pear-top">
          <div className="pear-top-left">
            {detail ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button onClick={detail.onBack} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 11px", borderRadius: 9, border: "1px solid #e6eaf1", background: "#fff", cursor: "pointer", font: "inherit", fontSize: 13, fontWeight: 600, color: "#475569" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                  Back
                </button>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-.2px", lineHeight: 1.1 }}>{detail.title}</div>
                  {detail.subtitle && <div style={{ fontSize: 11.5, color: "#94a3b8", fontFamily: "monospace" }}>{detail.subtitle}</div>}
                </div>
              </div>
            ) : (
              <Brand zana={zana} brand={brand} />
            )}
          </div>

          <div className="pear-top-right">{headerRight}</div>
        </header>

        <main className="pear-main">{children}</main>
      </div>
    </div>
  );
}
